import { Box, Grid, Button, TextField, Typography, Dialog, DialogTitle, DialogContent } from '@mui/material';
import { ArrayInput, NumberInput, SimpleFormIterator, TextInput, useNotify } from 'react-admin';
import { useState, useEffect } from 'react';
import axios, { AxiosError } from 'axios';
import constants from '../../constants';
import { getLocalizedErrorMessage } from '../../utils/functions';

const Departments = ({ record }: { record: any }) => {
	const [departmentName, setDepartmentName] = useState('');
	const [companyData, setCompanyData] = useState<any>(null);
	const [emailValues, setEmailValues] = useState<{ [key: string]: string }>({});
	const [selectedDepartment, setSelectedDepartment] = useState<any>(null);
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [departmentMembers, setDepartmentMembers] = useState<any[]>([]);
	const [newMemberEmail, setNewMemberEmail] = useState('');
	const notify = useNotify();

	useEffect(() => {
		const fetchCompanyData = async () => {
			try {
				const token = localStorage.getItem('token');
				if (!token) {
					notify('No authentication token found', { type: 'error' });
					return;
				}

				const response = await axios.get(
					`${constants.API_URL}company-departments/company/${record.id}`,
					{
						headers: {
							'Authorization': `Bearer ${token}`,
							'Content-Type': 'application/json'
						}
					}
				);

				setCompanyData(response.data.data);
				console.log('Company data:', response.data.data);
			} catch (error) {
				console.error('Error fetching company data:', error);
				notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
			}
		};

		if (record?.id) {
			fetchCompanyData();
		}
	}, [record?.id, notify]);

	const createDepartment = async () => {
		try {
			const token = localStorage.getItem('token');
			if (!token) {
				notify('No authentication token found', { type: 'error' });
				return;
			}

			await axios.post(
				`${constants.API_URL}company-departments`,
				{
					name: departmentName,
					company_id: record.id
				},
				{
					headers: {
						'Authorization': `Bearer ${token}`,
						'Content-Type': 'application/json'
					}
				}
			);

			notify('המחלקה נוצרה בהצלחה', { type: 'success' });
			setDepartmentName(''); // Clear the input after sending
			
			// Refresh departments data
			const refreshResponse = await axios.get(
				`${constants.API_URL}company-departments/company/${record.id}`,
				{
					headers: {
						'Authorization': `Bearer ${token}`,
						'Content-Type': 'application/json'
					}
				}
			);
			setCompanyData(refreshResponse.data.data);
		} catch (error) {
			console.error('Error creating department:', error);
			notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
		}
	};

	const deleteDepartment = async (departmentId: string, departmentName: string) => {
		if (window.confirm(`האם אתה בטוח שברצונך למחוק את המחלקה "${departmentName}"?`)) {
			try {
				const token = localStorage.getItem('token');
				if (!token) {
					notify('No authentication token found', { type: 'error' });
					return;
				}

				await axios.delete(
					`${constants.API_URL}company-departments/${departmentId}`,
					{
						headers: {
							'Authorization': `Bearer ${token}`,
							'Content-Type': 'application/json'
						}
					}
				);

				notify('המחלקה נמחקה בהצלחה', { type: 'success' });
				
				// Refresh departments data
				const refreshResponse = await axios.get(
					`${constants.API_URL}company-departments/company/${record.id}`,
					{
						headers: {
							'Authorization': `Bearer ${token}`,
							'Content-Type': 'application/json'
						}
					}
				);
				setCompanyData(refreshResponse.data.data);
			} catch (error) {
				console.error('Error deleting department:', error);
				notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
			}
		}
	};

	const assignManagerToDepartment = async (departmentId: string, departmentName: string, emails: string) => {
		if (!emails.trim()) {
			notify('אנא הכנס כתובת אימייל', { type: 'warning' });
			return;
		}

		try {
			const token = localStorage.getItem('token');
			if (!token) {
				notify('No authentication token found', { type: 'error' });
				return;
			}

			// Split emails by comma and trim whitespace
			const emailArray = emails.split(',').map(email => email.trim()).filter(email => email.length > 0);
			
			// Validate email format
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			const invalidEmails = emailArray.filter(email => !emailRegex.test(email));
			
			if (invalidEmails.length > 0) {
				notify(`כתובות אימייל לא תקינות: ${invalidEmails.join(', ')}`, { type: 'error' });
				return;
			}

			// Call API for each email
			for (const email of emailArray) {
				await axios.post(
					`${constants.API_URL}company-departments/${departmentId}/assign-manager`,
					{ 
						email,
						id: departmentId
					},
					{
						headers: {
							'Authorization': `Bearer ${token}`,
							'Content-Type': 'application/json'
						}
					}
				);
			}

			notify(`מנהלים הוקצו בהצלחה למחלקה ${departmentName}`, { type: 'success' });
			
			// Clear the email input for this department
			setEmailValues(prev => ({ ...prev, [departmentId]: '' }));
			
			// Refresh departments data
			const refreshResponse = await axios.get(
				`${constants.API_URL}company-departments/company/${record.id}`,
				{
					headers: {
						'Authorization': `Bearer ${token}`,
						'Content-Type': 'application/json'
					}
				}
			);
			setCompanyData(refreshResponse.data.data);
		} catch (error) {
			console.error('Error assigning manager:', error);
			notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
		}
	};

	const openDetailsDialog = async (department: any) => {
		setSelectedDepartment(department);
		setIsDialogOpen(true);
		
		// Fetch department members
		try {
			const token = localStorage.getItem('token');
			if (!token) {
				notify('No authentication token found', { type: 'error' });
				return;
			}

			const response = await axios.get(
				`${constants.API_URL}company-departments/${department._id}/members`,
				{
					headers: {
						'Authorization': `Bearer ${token}`,
						'Content-Type': 'application/json'
					}
				}
			);
            console.log(response.data.data);
			setDepartmentMembers(response.data.data || []);
		} catch (error) {
			console.error('Error fetching department members:', error);
			notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
			setDepartmentMembers([]);
		}
	};

	const closeDetailsDialog = () => {
		setSelectedDepartment(null);
		setIsDialogOpen(false);
		setDepartmentMembers([]);
	};

	const addMemberToDepartment = async (departmentId: string, departmentName: string, email: string) => {
		if (!email.trim()) {
			notify('אנא הכנס כתובת אימייל', { type: 'warning' });
			return;
		}

		// Validate email format
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(email)) {
			notify('כתובת אימייל לא תקינה', { type: 'error' });
			return;
		}

		try {
			const token = localStorage.getItem('token');
			if (!token) {
				notify('No authentication token found', { type: 'error' });
				return;
			}
console.log(record);
			await axios.post(
				`${constants.API_URL}/company-departments/${selectedDepartment._id}/add-member`,
				{ 
					email,
					department_id: departmentId,
                    company_id: record.id
				},
				{
					headers: {
						'Authorization': `Bearer ${token}`,
						'Content-Type': 'application/json'
					}
				}
			);

			notify(`עובד נוסף בהצלחה למחלקה ${departmentName}`, { type: 'success' });
			setNewMemberEmail(''); // Clear the input
			
			// Refresh department members
			const refreshResponse = await axios.get(
				`${constants.API_URL}company-departments/${departmentId}/members`,
				{
					headers: {
						'Authorization': `Bearer ${token}`,
						'Content-Type': 'application/json'
					}
				}
			);
			setDepartmentMembers(refreshResponse.data.data || []);
		} catch (error) {
			console.error('Error adding member:', error);
			notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
		}
	};

	const removeMemberFromDepartment = async (departmentId: string, departmentName: string, email: string) => {
		if (window.confirm(`האם אתה בטוח שברצונך להסיר את ${email} מהמחלקה ${departmentName}?`)) {
			try {
				const token = localStorage.getItem('token');
				if (!token) {
					notify('No authentication token found', { type: 'error' });
					return;
				}

				await axios.post(
					`${constants.API_URL}/company-departments/${departmentId}/remove-member`,
					{ 
						email,
						company_id: record.id
					},
					{
						headers: {
							'Authorization': `Bearer ${token}`,
							'Content-Type': 'application/json'
						}
					}
				);

				notify(`עובד הוסר בהצלחה מהמחלקה ${departmentName}`, { type: 'success' });
				
				// Refresh department members
				const refreshResponse = await axios.get(
					`${constants.API_URL}company-departments/${departmentId}/members`,
					{
						headers: {
							'Authorization': `Bearer ${token}`,
							'Content-Type': 'application/json'
						}
					}
				);
				setDepartmentMembers(refreshResponse.data.data || []);
			} catch (error) {
				console.error('Error removing member:', error);
				notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
			}
		}
	};

	const removeManagerFromDepartment = async (departmentId: string, departmentName: string, email: string) => {
		if (window.confirm(`האם אתה בטוח שברצונך להסיר את ${email} כמנהל מהמחלקה ${departmentName}?`)) {
			try {
				const token = localStorage.getItem('token');
				if (!token) {
					notify('No authentication token found', { type: 'error' });
					return;
				}

				await axios.post(
					`${constants.API_URL}/company-departments/${selectedDepartment._id}/remove-manager`,
					{ 
						email
					},
					{
						headers: {
							'Authorization': `Bearer ${token}`,
							'Content-Type': 'application/json'
						}
					}
				);

				notify(`מנהל הוסר בהצלחה מהמחלקה ${departmentName}`, { type: 'success' });
				
				// Refresh departments data to update managers list
				const refreshResponse = await axios.get(
					`${constants.API_URL}company-departments/company/${record.id}`,
					{
						headers: {
							'Authorization': `Bearer ${token}`,
							'Content-Type': 'application/json'
						}
					}
				);
				setCompanyData(refreshResponse.data.data);
				
				// Also refresh the selected department data in the dialog
				if (selectedDepartment && selectedDepartment._id === departmentId) {
					setSelectedDepartment(refreshResponse.data.data.find((dept: any) => dept._id === departmentId));
				}
			} catch (error) {
				console.error('Error removing manager:', error);
				notify(getLocalizedErrorMessage(error as AxiosError), { type: 'error' });
			}
		}
	};

	return (
		<Box>
			<Box sx={{ 
				border: '1px solid #e0e0e0', 
				borderRadius: '8px', 
				padding: '16px', 
				mb: 3,
				backgroundColor: '#fafafa'
			}}>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Typography variant="h6">הוספת מחלקה</Typography>
					</Grid>
					<Grid item xs={8}>
						<TextField
							fullWidth
							label="שם המחלקה"
							value={departmentName}
							onChange={(e) => setDepartmentName(e.target.value)}
							onKeyPress={(e) => {
								if (e.key === 'Enter') {
									createDepartment();
								}
							}}
						/>
					</Grid>
					<Grid item xs={4}>
						<Button
							variant="contained"
							color="primary"
							onClick={createDepartment}
							disabled={!departmentName.trim()}
							fullWidth
							sx={{ height: '56px' }}
						>
							שלח
						</Button>
					</Grid>
				</Grid>
			</Box>
			
			{/* Departments List */}
			{companyData && companyData.length > 0 && (
				<Box sx={{ 
					border: '1px solid #e0e0e0', 
					borderRadius: '8px', 
					padding: '16px', 
					mb: 3,
					backgroundColor: '#fafafa'
				}}>
					<Typography variant="h6" sx={{ mb: 3, color: '#1976d2', fontWeight: 'bold' }}>
						📋 רשימת מחלקות ({companyData.length})
					</Typography>
					<Grid container spacing={2}>
						{companyData.map((department: any, index: number) => (
							<Grid item xs={12} key={department._id || index}>
								<Box sx={{ 
									display: 'flex', 
									alignItems: 'center', 
									gap: 2,
									padding: '16px',
									border: '1px solid #ddd',
									borderRadius: '8px',
									backgroundColor: 'white',
									boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
									transition: 'all 0.2s ease',
									'&:hover': {
										boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
										transform: 'translateY(-1px)'
									}
								}}>
									<Box sx={{ 
										display: 'flex', 
										flexDirection: 'column', 
										minWidth: '200px',
										padding: '8px 12px',
										backgroundColor: '#f8f9fa',
										borderRadius: '6px',
										border: '1px solid #e9ecef'
									}}>
										<Typography sx={{ fontWeight: 'bold', color: '#495057', fontSize: '1rem' }}>
											🏢 {department.name}
										</Typography>
										<Typography variant="caption" sx={{ color: '#6c757d' }}>
											מחלקה #{index + 1}
										</Typography>
									</Box>
									<Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
										<TextField
											size="small"
											placeholder="📧 הכנס אימייל של מנהל מחלקה (מופרד בפסיקים למספר מנהלים)"
											value={emailValues[department._id] || ''}
											onChange={(e) => {
												setEmailValues(prev => ({
													...prev,
													[department._id]: e.target.value
												}));
											}}
											sx={{
												'& .MuiOutlinedInput-root': {
													backgroundColor: '#ffffff'
												}
											}}
										/>
									</Box>
									<Box sx={{ display: 'flex', gap: 1 }}>
										<Button
											variant="contained"
											size="small"
											onClick={() => assignManagerToDepartment(department._id, department.name, emailValues[department._id] || '')}
											sx={{
												backgroundColor: '#28a745',
												'&:hover': {
													backgroundColor: '#218838'
												}
											}}
										>
											👤 הקצה מנהל
										</Button>
										<Button
											variant="outlined"
											size="small"
											onClick={() => openDetailsDialog(department)}
											sx={{
												borderColor: '#007bff',
												color: '#007bff',
												'&:hover': {
													borderColor: '#0056b3',
													backgroundColor: '#f8f9fa'
												}
											}}
										>
											📊 פרטים
										</Button>
										<Button
											variant="outlined"
											color="error"
											size="small"
											onClick={() => deleteDepartment(department._id, department.name)}
											sx={{
												'&:hover': {
													backgroundColor: '#f8d7da'
												}
											}}
										>
											🗑️ מחק
										</Button>
									</Box>
								</Box>
							</Grid>
						))}
					</Grid>
				</Box>
			)}

			{/* Details Dialog */}
			<Dialog 
				open={isDialogOpen} 
				onClose={closeDetailsDialog}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>
					פרטי מחלקה: {selectedDepartment?.name} 
				</DialogTitle>
				<DialogContent>
					{selectedDepartment && (
						<Box sx={{ p: 2 }}>
							<Typography variant="h6" sx={{ mb: 2 }}>
						מנהלי מחלקה:
							</Typography>
							{selectedDepartment.managers && selectedDepartment.managers.length > 0 ? (
								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
									{selectedDepartment.managers.map((manager: any, index: number) => (
										<Box
											key={index}
											sx={{
												backgroundColor: '#e3f2fd',
												padding: '8px 12px',
												borderRadius: '16px',
												border: '1px solid #bbdefb',
												fontSize: '0.875rem',
												display: 'flex',
												alignItems: 'center',
												gap: 1
											}}
										>
											<span>{manager || 'Unknown'}</span>
											<Button
												variant="text"
												color="error"
												size="small"
												onClick={() => removeManagerFromDepartment(selectedDepartment._id, selectedDepartment.name, manager)}
												sx={{
													minWidth: 'auto',
													padding: '2px',
													'&:hover': {
														backgroundColor: '#ffebee'
													}
												}}
											>
												✕
											</Button>
										</Box>
									))}
								</Box>
							) : (
								<Typography color="text.secondary">
									אין מנהלים מוקצים למחלקה זו
								</Typography>
							)}
							
							<Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
								עובדים:
							</Typography>
							
							{/* Add Member Section */}
							<Box sx={{ 
								display: 'flex', 
								gap: 1, 
								mb: 2,
								padding: '12px',
								backgroundColor: '#f8f9fa',
								borderRadius: '6px',
								border: '1px solid #e9ecef'
							}}>
								<TextField
									size="small"
									placeholder="📧 הכנס אימייל של עובד להוספה"
									value={newMemberEmail}
									onChange={(e) => setNewMemberEmail(e.target.value)}
									onKeyPress={(e) => {
										if (e.key === 'Enter') {
											addMemberToDepartment(selectedDepartment._id, selectedDepartment.name, newMemberEmail);
										}
									}}
									sx={{ flexGrow: 1 }}
								/>
								<Button
									variant="contained"
									size="small"
									onClick={() => addMemberToDepartment(selectedDepartment._id, selectedDepartment.name, newMemberEmail)}
									sx={{
										backgroundColor: '#007bff',
										'&:hover': {
											backgroundColor: '#0056b3'
										}
									}}
								>
									➕ הוסף עובד
								</Button>
							</Box>
							
							{departmentMembers.length > 0 ? (
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
									{departmentMembers.map((member: any, index: number) => (
										<Box
											key={index}
											sx={{
												backgroundColor: '#f8f9fa',
												padding: '12px',
												borderRadius: '8px',
												border: '1px solid #e9ecef',
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center'
											}}
										>
											<Box>
												<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
													👤 {member.first_name} {member.last_name || ''}
												</Typography>
												<Typography variant="body2" color="text.secondary">
													📧 {member.email}
												</Typography>
												{member.departments?.some((dept: any) => 
													dept.department_id === selectedDepartment._id && dept.is_manager
												) && (
													<Typography variant="caption" sx={{ 
														color: '#28a745', 
														fontWeight: 'bold',
														backgroundColor: '#d4edda',
														padding: '2px 6px',
														borderRadius: '4px'
													}}>
														👑 מנהל
														
													</Typography>
												)}
											</Box>
											<Button
												variant="outlined"
												color="error"
												size="small"
												onClick={() => removeMemberFromDepartment(selectedDepartment._id, selectedDepartment.name, member.email)}
												sx={{
													'&:hover': {
														backgroundColor: '#f8d7da'
													}
												}}
											>
												🗑️ הסר
											</Button>
										</Box>
									))}
								</Box>
							) : (
								<Typography color="text.secondary">
									אין עובדים במחלקה זו
								</Typography>
							)}
						</Box>
					)}
				</DialogContent>
			</Dialog>
		</Box>
	);
};

export default Departments;
